import { Button, Select, Text, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Section } from "@/components/ui"
import { useOrderLots, useSetOrderLots } from "@/lib/lots"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"

/**
 * Order page → Lots: which lot shipped on each line. Filled automatically at fulfilment with each
 * option's current lot; staff can change it here (the customer's account shows the lot and its COA).
 */
export function OrderLotsCard({ orderId }: { orderId: string }) {
  const { data } = useOrderLots(orderId)
  const set = useSetOrderLots(orderId)
  const canWrite = useCan()("orders:write")
  const [picks, setPicks] = useState<Record<string, string>>({})
  useEffect(() => {
    if (data) setPicks(Object.fromEntries(data.lines.map((l) => [l.line_item_id, l.lots[0]?.id ?? ""])))
  }, [data])
  if (!data?.lines.some((l) => l.variant_id)) return null
  const dirty = data.lines.some((l) => (picks[l.line_item_id] ?? "") !== (l.lots[0]?.id ?? ""))
  return (
    <Section title="Lots">
      <div className="flex flex-col gap-3 p-4">
        {data.lines
          .filter((l) => l.variant_id)
          .map((l) => (
            <div key={l.line_item_id}>
              <Text size="small" weight="plus" className="truncate">{l.title}</Text>
              {canWrite && l.choices.length ? (
                <Select value={picks[l.line_item_id] || "none"} onValueChange={(v) => setPicks({ ...picks, [l.line_item_id]: v === "none" ? "" : v })}>
                  <Select.Trigger className="mt-1"><Select.Value /></Select.Trigger>
                  <Select.Content>
                    <Select.Item value="none">No lot</Select.Item>
                    {l.choices.map((c) => <Select.Item key={c.id} value={c.id}>{c.lot_number}{c.is_current ? " (current)" : ""}</Select.Item>)}
                  </Select.Content>
                </Select>
              ) : (
                <Text size="small" className="font-mono text-ui-fg-subtle">{l.lots.map((x) => x.lot_number).join(", ") || "No lot"}</Text>
              )}
              {l.lots[0]?.source === "auto" ? <Text size="xsmall" className="text-ui-fg-subtle">Set at fulfilment (current lot)</Text> : null}
            </div>
          ))}
        {canWrite && dirty ? (
          <Button
            size="small"
            isLoading={set.isPending}
            onClick={async () => {
              try {
                await set.mutateAsync(data.lines.filter((l) => l.variant_id).map((l) => ({ line_item_id: l.line_item_id, lot_ids: picks[l.line_item_id] ? [picks[l.line_item_id]] : [] })))
                toast.success("Lots saved")
              } catch (e) {
                toast.error(errorMessage(e))
              }
            }}
          >
            Save lots
          </Button>
        ) : null}
        <Link to="/products/lots" className="text-[12px] text-ui-fg-interactive hover:underline">Manage lots and COAs</Link>
      </div>
    </Section>
  )
}
